import { HTMLAttributes, ReactNode } from 'react';

import clsx from 'clsx';
import { ReadonlyURLSearchParams } from 'next/navigation';

import RichTextEditorLink from '../../RichTextEditorLink';
import getRichTextUrl from 'ui/RichTextEditorContentRender/getRichTextUrl';
import RichTextEditorCTALink from 'ui/RichTextEditorCTALink';

interface ElementAttributes extends HTMLAttributes<HTMLElement> {}

type ElementProps = {
  attributes: ElementAttributes;
  children: ReactNode;
  element: any;
  searchParams?: ReadonlyURLSearchParams;
  hostName?: string;
};

function Element(props: ElementProps) {
  const { attributes, children, element, searchParams, hostName } = props;
  const style = { textAlign: element.align };

  switch (element.type) {
    case 'block-quote':
      return (
        <blockquote
          style={style}
          className="pl-3 italic border-l-2 border-slate-400 text-slate-400"
          {...attributes}
        >
          {children}
        </blockquote>
      );
    case 'bulleted-list':
      return (
        <ul style={style} className="pl-10 mt-4 list-disc" {...attributes}>
          {children}
        </ul>
      );
    case 'heading-one':
      return (
        <h1 style={style} className="min-h-6" {...attributes}>
          {children}
        </h1>
      );
    case 'heading-two':
      return (
        <h2 style={style} className="min-h-6" {...attributes}>
          {children}
        </h2>
      );
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case 'numbered-list':
      return (
        <ol style={style} className="pl-10 mt-4 list-decimal" {...attributes}>
          {children}
        </ol>
      );
    case 'code':
      return (
        <code
          style={style}
          {...attributes}
          className={clsx(`${element.children[0].code ? 'bg-slate-200' : 'bg-white'}`)}
        >
          {children}
        </code>
      );
    case 'paragraph':
      return (
        <p {...attributes} className="min-h-6">
          {children}
        </p>
      );
    case 'link': {
      const updatedURL = getRichTextUrl(element?.href, searchParams, hostName);
      const updatedElement = { ...element, href: updatedURL };
      return <RichTextEditorLink element={updatedElement}>{props.children}</RichTextEditorLink>;
    }
    case 'cta': {
      const updatedURL = getRichTextUrl(element?.href, searchParams, hostName);
      const updatedElement = { ...element, href: updatedURL };
      return (
        <RichTextEditorCTALink element={updatedElement}>{props.children}</RichTextEditorCTALink>
      );
    }
    default:
      return (
        <p {...attributes} className="min-h-6">
          {children}
        </p>
      );
  }
}

export default Element;
